import { fileDeleteAtom } from '@rematter/modules/customer-portal/_graphql/mutationFileDelete';
import { ensureValue, uploadFiles } from '@rematter/ui-web';
import { atom } from 'jotai';

import { confirmFileUploadAtom } from '../_graphql/mutationFileConfirmUpload';
import { purchaseOrderFileCreateAtom } from '../_graphql/mutationFileCreate';
import { purchaseOrderUpdateAtom } from '../_graphql/mutationPurchaseOrderUpdate';
import { purchaseOrderDetailsQueryAtom } from '../_graphql/queryPurchaseOrderDetails';
import { customerPortalRoutes } from '../routes';
import { PurchaseOrderDetailsTab } from '../types';

import { $user } from '~/shared/providers/auth/identity.localState';
import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $tab = atomFromQueryString.string<PurchaseOrderDetailsTab>('tab');
export const $purchaseOrderDetailsParams = atomFromRoutePath(
  customerPortalRoutes.purchaseOrderDetails,
);
export const $purchaseOrderId = atom(
  (get) => get($purchaseOrderDetailsParams)?.purchaseOrderId,
);

// 🔘🔘🔘 PLAIN ATOMS
const $uploadProgress = atom({ isUploading: false, progress: 0 });

// 🟢🟢🟢 COMPUTED DATA -> upload progress
export const $getUploadProgress = atom((get) => get($uploadProgress));

// 🟢🟢🟢 REMOTE DATA
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore TS7056
export const $purchaseOrderDetails = purchaseOrderDetailsQueryAtom((get) => ({
  variables: {
    id: get($purchaseOrderId),
  },
  skip: !get($purchaseOrderId),
}));

// 🟠🟠🟠 ACTION -> UPDATE PURCHASE ORDER
export const $updatePurchaseOrder = purchaseOrderUpdateAtom({
  onSuccess: (get) => get($purchaseOrderDetails).refetch(),
});

// 🟠🟠🟠 ACTION -> CREATE file
export const $createPurchaseOrderFile = purchaseOrderFileCreateAtom();

// 🟠🟠🟠 ACTION -> CONFIRM upload
export const $purchaseOrderConfirmUpload = confirmFileUploadAtom({
  onSuccess: (get) => get($purchaseOrderDetails).refetch(),
});

// 🟠🟠🟠 ACTION -> DELETE file
export const $deletePurchaseOrderFile = fileDeleteAtom({
  onSuccess: (get) => get($purchaseOrderDetails).refetch(),
});

// 🟠🟠🟠 ACTION -> ADD load attachments
export const $addPurchaseOrderAttachments = atom(
  null,
  async (get, set, files: File[]) => {
    const user = get($user);

    set($uploadProgress, { isUploading: true, progress: 0 });

    try {
      await uploadFiles(files, {
        userId: ensureValue(user?.id),
        onProgress: (progress) => {
          set($uploadProgress, { isUploading: true, progress });
        },
        createFile: (params) =>
          set($createPurchaseOrderFile, {
            ...params,
            purchaseOrderId: ensureValue(get($purchaseOrderId)),
          }),
        confirmUpload: (params) => set($purchaseOrderConfirmUpload, params),
      });
    } finally {
      // Reset upload progress
      set($uploadProgress, { isUploading: false, progress: 0 });
    }
  },
);
